/**
 * Фотография товара Wildberries по его номеру.
 *
 * АДРЕС СЧИТАЕТСЯ ИЗ НОМЕРА, А НЕ ЗАПРАШИВАЕТСЯ. Картинки лежат
 * на корзинах `basket-NN.wbbasket.ru` по вычислимому пути; отдельного
 * метода за ними ходить не нужно вовсе — а значит, не нужен ни ключ,
 * ни квота, ни выгрузка. Приём взят из трекера, где он работает
 * на живых данных.
 *
 * НОМЕР КОРЗИНЫ БЕРЁТСЯ ИЗ ТАБЛИЦЫ WILDBERRIES. Ширина диапазонов
 * различается: продолжение старой таблицы формулой /258 направляло
 * новые товары на чужой сервер. Соседние корзины остаются запасными
 * кандидатами — браузер перебирает их сам, пока
 * картинка не загрузится. Удачная корзина запоминается для соседних
 * номеров: они почти всегда лежат вместе.
 *
 * ЗАГЛУШКИ НЕТ НАМЕРЕННО. Не нашлось фото — остаётся пустое место
 * той же величины: рамка «нет фото» отвлекает ровно так же, как само
 * фото, но не несёт ничего.
 */

/** Корзины, известные на 09.08.2026. Перебираются, если угадать не вышло. */
const KNOWN = [43, 44, 45, 42, 41, 40, 21, 26, 30, 35, 37, 17];

/** Сколько шагов в обе стороны от угаданной корзины пробовать. */
const SPREAD = 8;

const MIN_HOST = 1;
const MAX_HOST = 70;

/**
 * Последний vol каждой корзины, от basket-01 до basket-65.
 *
 * Источник: https://cdn.wbbasket.ru/api/v3/upstreams,
 * origin.mediabasket_route_map, снято 08.10.2026.
 * Например, товар 1592994884 находится на basket-49, а 1726199881
 * на basket-50. Старая формула давала обоим 60; запасной перебор
 * начинался с 52 и до нужных серверов не доходил.
 */
const RANGE_ENDS = [
  143, 287, 431, 719, 1007, 1061, 1115, 1169, 1313, 1601, 1655, 1919,
  2045, 2189, 2405, 2621, 2837, 3053, 3269, 3485, 3701, 3917, 4133, 4349, 4565,
  4877, 5189, 5501, 5813, 6125, 6437, 6749, 7061, 7373, 7685, 7997, 8309, 8741,
  9173, 9605, 10373, 11141, 11909, 12677, 13445, 14213, 14981, 15749, 16517, 17285,
  18053, 18821, 19589, 20357, 21125, 21893, 22661, 23429, 24197, 24965, 25733,
  26501, 27269, 28037, 28805,
] as const;

/** Корзина по таблице WB; за её пределами — ограниченная оценка. */
export function guessHost(nm: number): number {
  const vol = Math.floor(nm / 100_000);
  const index = RANGE_ENDS.findIndex((end) => vol <= end);
  if (index >= 0) return index + 1;
  // Последние известные диапазоны имеют ширину 768 vol. Будущая
  // раскладка неизвестна; это только старт для конечного перебора.
  const last = RANGE_ENDS[RANGE_ENDS.length - 1]!;
  return Math.min(MAX_HOST, RANGE_ENDS.length + Math.ceil((vol - last) / 768));
}

/**
 * Диапазон, в котором товары лежат вместе.
 *
 * ЗАПОМИНАТЬ КОРЗИНУ НАДО ПО ДИАПАЗОНУ, А НЕ НА ВСЮ СТРАНИЦУ. Первая
 * редакция держала одну корзину на все картинки сразу — и товар
 * из другого диапазона получал чужую корзину первой попыткой, промахивался
 * и уходил в долгий перебор. Соседние номера лежат вместе, далёкие — нет.
 */
export function bucketOf(nm: number): number {
  return Math.floor(Math.floor(nm / 100_000) / 100);
}

/**
 * Кандидаты в порядке убывания надежды.
 *
 * `memo` — корзина, уже сработавшая для соседа по диапазону: она
 * попадает с первого раза чаще угаданной.
 */
export function hostsFor(nm: number, memo?: number): number[] {
  const guess = guessHost(nm);
  const list: number[] = [];
  if (memo) list.push(memo);
  list.push(guess);
  for (let d = 1; d <= SPREAD; d++) {
    list.push(guess + d);
    list.push(guess - d);
  }
  list.push(...KNOWN);
  return [...new Set(list)].filter((h) => h >= MIN_HOST && h <= MAX_HOST);
}

export type PhotoSize = 'c246x328' | 'big';

export function photoUrl(nm: number, host: number, size: PhotoSize, jpg = false): string {
  const vol = Math.floor(nm / 100_000);
  const part = Math.floor(nm / 1000);
  const h = String(host).padStart(2, '0');
  return `https://basket-${h}.wbbasket.ru/vol${vol}/part${part}/${nm}/images/${size}/1.${jpg ? 'jpg' : 'webp'}`;
}

/**
 * Следующая попытка после неудачи.
 *
 * На каждой корзине пробуются ОБА формата: у части карточек webp нет,
 * есть только jpg. Кончились корзины — возвращаем `null`, и картинка
 * убирается совсем.
 */
export function nextAttempt(
  attempt: { host: number; jpg: boolean },
  hosts: number[],
): { host: number; jpg: boolean } | null {
  if (!attempt.jpg) return { host: attempt.host, jpg: true };

  const i = hosts.indexOf(attempt.host);
  const next = hosts[i + 1];
  return next === undefined ? null : { host: next, jpg: false };
}

/** Ссылка на карточку товара — из фотографии всегда хотят перейти к товару. */
export function cardUrl(nm: number): string {
  return `https://www.wildberries.ru/catalog/${nm}/detail.aspx`;
}
